import React, { useState } from "react";
import Layout from "../../layout/Main/Layout";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Grid,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Send as SendIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { post } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";

const BroadcastNotification = () => {
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  
  // Notification form state
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    scheduledFor: '',
    type: 'GENERAL'
  });

  // Send notification to all users
  const handleSendNotification = async () => {
    try {
      if (!notificationForm.title || !notificationForm.message) {
        toastMessage("Please fill in title and message", "warning");
        return;
      }

      setLoading(true);
      
      const response = await post("/api/dashboard/broadcast-notification", {
        title: notificationForm.title,
        message: notificationForm.message,
        scheduledFor: notificationForm.scheduledFor,
        type: notificationForm.type
      });
      
      if (response?.success) {
        setLastResult(response.data);
        
        if (notificationForm.scheduledFor) {
          toastMessage(`Notification scheduled successfully for ${new Date(notificationForm.scheduledFor).toLocaleString()}!`, "success");
        } else {
          toastMessage(`Notification sent to ${response.data.successCount} users!`, "success");
        }
        
        setConfirmOpen(false);
        // Reset form
        setNotificationForm({
          title: '',
          message: '',
          scheduledFor: '',
          type: 'GENERAL'
        });
      }
    } catch (error) {
      console.error("Error sending notification:", error);
      toastMessage("Failed to send notification", "error");
    } finally {
      setLoading(false);
    }
  };

  // Format scheduled time for display
  const formatScheduledTime = (scheduledFor) => {
    if (!scheduledFor) return "Send immediately";
    const date = new Date(scheduledFor);
    const now = new Date();
    
    if (date <= now) {
      return "Send immediately";
    }
    
    return `Scheduled for ${date.toLocaleString()}`;
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Broadcast Notification
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Send push notifications to all app users
            </Typography>
          </Box>
          <NotificationsIcon sx={{ fontSize: 40, color: "#1976d2" }} />
        </Box>

        {/* Last Result Card */}
        {lastResult && (
          <Card sx={{ mb: 3, bgcolor: lastResult.scheduledFor ? 'warning.50' : 'success.50', borderLeft: 4, borderColor: lastResult.scheduledFor ? 'warning.main' : 'success.main' }}>
            <CardContent>
              <Typography variant="h6" color={lastResult.scheduledFor ? "warning.main" : "success.main"} gutterBottom>
                {lastResult.scheduledFor ? "📅 Last Notification Scheduled Successfully" : "✅ Last Notification Sent Successfully"}
              </Typography>
              
              {lastResult.scheduledFor ? (
                // Scheduled notification display
                <Box>
                  <Typography variant="body2" gutterBottom>
                    <strong>Title:</strong> {lastResult.title}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Scheduled for:</strong> {new Date(lastResult.scheduledFor).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Status:</strong> {lastResult.status}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Type:</strong> {lastResult.type}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    Notification ID: {lastResult.notificationId}
                  </Typography>
                </Box>
              ) : (
                // Sent notification display
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Total Users</Typography>
                    <Typography variant="h6">{lastResult.totalUsers}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Successfully Sent</Typography>
                    <Typography variant="h6" color="success.main">{lastResult.successCount}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Failed</Typography>
                    <Typography variant="h6" color="error.main">{lastResult.failureCount}</Typography>
                  </Grid>
                </Grid>
              )}
              
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {lastResult.scheduledFor ? "Scheduled" : "Sent"} at: {new Date().toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Main Form Card */}
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PeopleIcon color="primary" />
              Send Notification to All Users
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              This will send a push notification to ALL users who have the mobile app installed and notifications enabled.
            </Alert>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Title Field */}
              <TextField
                label="Notification Title"
                value={notificationForm.title}
                onChange={(e) => setNotificationForm(prev => ({ ...prev, title: e.target.value }))}
                fullWidth
                required
                placeholder="e.g., App Update Available"
                helperText="Keep it short and clear (max 50 characters recommended)"
                inputProps={{ maxLength: 100 }}
              />
              
              {/* Message Field */}
              <TextField
                label="Notification Message"
                value={notificationForm.message}
                onChange={(e) => setNotificationForm(prev => ({ ...prev, message: e.target.value }))}
                multiline
                rows={3}
                fullWidth
                required
                placeholder="e.g., We've added new features to improve your experience. Update now!"
                helperText="Clear and actionable message (max 200 characters recommended)"
                inputProps={{ maxLength: 500 }}
              />
              
              {/* Schedule Time and Type */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  label="Schedule Notification"
                  type="datetime-local"
                  value={notificationForm.scheduledFor}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, scheduledFor: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  helperText="Leave empty to send immediately"
                  sx={{ minWidth: 250 }}
                />
                
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Notification Type</InputLabel>
                  <Select
                    value={notificationForm.type}
                    onChange={(e) => setNotificationForm(prev => ({ ...prev, type: e.target.value }))}
                    label="Notification Type"
                  >
                    <MenuItem value="GENERAL">General</MenuItem>
                    <MenuItem value="UPDATE">App Update</MenuItem>
                    <MenuItem value="MAINTENANCE">Maintenance</MenuItem>
                    <MenuItem value="FEATURE">New Feature</MenuItem>
                    <MenuItem value="ANNOUNCEMENT">Announcement</MenuItem>
                    <MenuItem value="EMERGENCY">Emergency</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Preview */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Notification Preview:
                </Typography>
                <Card sx={{ p: 2, bgcolor: 'grey.50', border: 1, borderColor: 'grey.300' }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      bgcolor: 'primary.main', 
                      borderRadius: 1, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <NotificationsIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {notificationForm.title || "Notification Title"}
                        </Typography>
                        <Chip 
                          label={formatScheduledTime(notificationForm.scheduledFor)} 
                          size="small" 
                          color={notificationForm.scheduledFor ? "warning" : "success"}
                          icon={<ScheduleIcon />}
                        />
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        {notificationForm.message || "Notification message will appear here..."}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        GutBuddy • now
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Box>

              {/* Send Button */}
              <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={notificationForm.scheduledFor ? <ScheduleIcon /> : <SendIcon />}
                  onClick={() => setConfirmOpen(true)}
                  disabled={!notificationForm.title || !notificationForm.message || loading}
                  sx={{ 
                    bgcolor: notificationForm.scheduledFor ? "#ed6c02" : "#1976d2", 
                    px: 4, 
                    py: 1.5,
                    fontSize: '1.1rem'
                  }}
                >
                  {notificationForm.scheduledFor ? "Schedule Notification" : "Send to All Users"}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {notificationForm.scheduledFor ? <ScheduleIcon color="warning" /> : <WarningIcon color="warning" />}
              {notificationForm.scheduledFor ? "Confirm Scheduled Notification" : "Confirm Broadcast Notification"}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {notificationForm.scheduledFor 
                ? "You are about to schedule a push notification to ALL users." 
                : "You are about to send a push notification to ALL users. This action cannot be undone."
              }
            </Alert>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Notification Details:</Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2"><strong>Title:</strong> {notificationForm.title}</Typography>
                <Typography variant="body2"><strong>Message:</strong> {notificationForm.message}</Typography>
                <Typography variant="body2"><strong>Schedule:</strong> {formatScheduledTime(notificationForm.scheduledFor)}</Typography>
                <Typography variant="body2"><strong>Type:</strong> {notificationForm.type}</Typography>
              </Box>
            </Box>
            
            <Typography variant="body2" color="textSecondary">
              This notification will be sent to all users who have:
              • The mobile app installed
              • Push notifications enabled
              • Valid FCM tokens
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSendNotification}
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : (notificationForm.scheduledFor ? <ScheduleIcon /> : <SendIcon />)}
            >
              {loading 
                ? (notificationForm.scheduledFor ? "Scheduling..." : "Sending...") 
                : (notificationForm.scheduledFor ? "Schedule Notification" : "Send Notification")
              }
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default BroadcastNotification;
